import React from 'react';
import { GetServerSideProps } from 'next';
import config from 'config';
import AppraisalFunnel from '@domain-group/fe-pa-appraisal-funnel';
import { Page } from '../../../components';
import { DigitalData } from '../../../@types';
import createMetaTitle from '../../../utils/appraisal/meta-title';
import { getUserDetails, serializeNextProps } from '../../../utils';

import { retrieveAddress } from '../../../services/property-profile-service';
import { getAgentsFromIds } from '../../../services/agency-service';
import { AppPageProps } from '../../_app';
import { createDigitalData } from '../../../components/head/utils';
import { getAppraisalFFPercentage } from '../../../services/feature-flag-services';
import { getAppraisalVersion } from '../../../utils/appraisal/service/utils';
import { AppraisalVersion } from '../../../utils/appraisal/service/types';

const EntryStep = {
  Address: 'address',
  GetPropertyDetails: 'get-property-details',
  PropertyDetails: 'property-details',
  Renovations: 'renovations',
  SpecialFeatures: 'special-features',
  PriceEstimate: 'price-estimate',
  SellTime: 'sell-time',
  PropertyUse: 'property-use',
  Contact: 'contact',
  Agents: 'agents',
  SendAppraisal: 'send-appraisal',
  Confirmation: 'confirmation',
};

interface AppraisalProps extends AppPageProps {
  appName: string;
  mixpanelToken: string;
  title: string;
  description: string;
  metaTitle: string;
  metaDescription: string;
  metaImage: string;
  digitalData: DigitalData;

  // Funnel prop types
  graphqlEndpoint?: string;

  prefilledFunnelData: {
    address?: {
      property?: {
        id?: string;
        label?: string;
        urlSlug?: string;
      };
    };
    agents?: {
      selected?: {
        name: string;
        id: string;
        avatar?: string;
      }[];
    };
    contact?: {
      firstName?: string;
      lastName?: string;
      email?: string;
      phone?: string;
      preferredContactTime?: string;
    };
  };
  baseUrl: string;
  authUrl: string;
  validatePhoneNoEndpoint: string;
  exitUrl?: string;
  baseName?: string;
  enquiryEndpoint?: string;
  claimEndpoint?: string;
  reClaimEndpoint?: string;
  myPropertyEndpoint?: string;
  downloadPropertyReportEndpoint?: string;
  landing?: boolean;
  userId?: string;
  sessionId?: string;
  isWebView?: boolean;
  entryStep?: string;
  entryConfidence?: string;
  targetAppraisalVersion: AppraisalVersion;
  trackingDisabled: boolean;
  useCookieOnClient?: boolean;
  authData?: {
    isAuthenticated?: boolean;
    email?: string;
    userImageSrc?: string;
    memberFirstName?: string;
  };
}

const Appraisal = ({
  appName,
  title,
  description,
  metaTitle,
  metaDescription,
  metaImage,
  digitalData,

  mixpanelToken,
  baseUrl,
  authUrl,
  validatePhoneNoEndpoint,
  baseName,
  userId,
  sessionId,
  exitUrl,
  enquiryEndpoint,
  claimEndpoint,
  reClaimEndpoint,
  myPropertyEndpoint,
  downloadPropertyReportEndpoint,
  graphqlEndpoint,
  entryStep,
  entryConfidence,
  targetAppraisalVersion,
  prefilledFunnelData,
  isWebView,
  landing,
  trackingDisabled,
  useCookieOnClient,
  authData,
}: AppraisalProps): JSX.Element | null => (
  <Page
    title={title}
    description={description}
    metaTitle={metaTitle}
    metaDescription={metaDescription}
    metaImage={metaImage}
    digitalData={digitalData}
    trackingDisabled={trackingDisabled}
    doNotIndex
  >
    <AppraisalFunnel
      theme="domain"
      mixpanelToken={mixpanelToken}
      baseUrl={baseUrl}
      authUrl={authUrl}
      validatePhoneNoEndpoint={validatePhoneNoEndpoint}
      baseName={baseName}
      userId={userId}
      sessionId={sessionId}
      exitUrl={exitUrl}
      enquiryEndpoint={enquiryEndpoint}
      claimEndpoint={claimEndpoint}
      reClaimEndpoint={reClaimEndpoint}
      myPropertyEndpoint={myPropertyEndpoint}
      downloadPropertyReportEndpoint={downloadPropertyReportEndpoint}
      graphqlEndpoint={graphqlEndpoint}
      entryStep={entryStep}
      entryConfidence={entryConfidence}
      prefilledFunnelData={prefilledFunnelData}
      isWebView={isWebView}
      landing={landing}
      useCookieOnClient={useCookieOnClient}
      authData={authData}
      appraisalVersion={targetAppraisalVersion}
      graphqlClientName={appName}
    />
  </Page>
);

export default Appraisal;

export const getServerSideProps: GetServerSideProps<AppraisalProps> = async ({
  req,
  res,
  query,
}) => {
  const appName = config.get<string>('APP_NAME');

  const user = getUserDetails(req.user);

  // get the experiment percentage weight from feature flag service
  const experimentPercentWeight = await getAppraisalFFPercentage();

  // init appraisal version as 'v3' (control) by default
  let targetAppraisalVersion = AppraisalVersion.v3;

  // randomize only when experimentPercentWeight is a valid percent greater than 0
  if (
    typeof experimentPercentWeight === 'number' &&
    experimentPercentWeight > 0
  ) {
    // get a random percent between 1 and 100;
    const randomPercent = Math.floor(Math.random() * 100) + 1;

    // check if forceExperiment query parameter is set to 'true'
    const forceExperiment =
      typeof req.query.forceExperiment === 'string' &&
      req.query.forceExperiment.toLowerCase() === 'true';

    targetAppraisalVersion = getAppraisalVersion(
      randomPercent,
      experimentPercentWeight,
      forceExperiment,
    );
  }

  // Entry step is configurable via query parameter - this overrides the "kick user back to start"
  // behaviour below, because we assume the user has been redirected to the correct step along with
  // the other required query parameters to prefill any data from earlier steps (eg. address)
  const entryStep =
    typeof query.entryStep === 'string'
      ? decodeURI(query.entryStep)
      : undefined;

  // We don't persist the form state across server-side renders, so we need to
  // redirect to the first step if the user has tried to access one of the later steps
  if (
    entryStep &&
    ![EntryStep.Address, EntryStep.GetPropertyDetails].includes(entryStep)
  ) {
    return {
      redirect: {
        destination: '/services/appraisal',
        permanent: false,
      },
    };
  }

  // Fetch address and agents if found in parameters passed
  // TODO: We could log the errors here, though it's not critical
  const [propertyAddress, agents] = await Promise.all([
    typeof query.address === 'string' && res.locals?.accessToken
      ? retrieveAddress(res.locals?.accessToken, query.address).catch(
          () => undefined,
        )
      : undefined,
    query.agentId && res.locals?.accessToken
      ? getAgentsFromIds(
          typeof query.agentId === 'string'
            ? query.agentId.split(',')
            : query.agentId,
          res.locals.accessToken,
        ).catch(() => undefined)
      : undefined,
  ]);

  // In order to skip to the address step, we need to have a valid address prefilled
  // (Otherwise the funnel will not know which property is being enquired about!)
  if (
    entryStep &&
    entryStep === EntryStep.GetPropertyDetails &&
    !propertyAddress?.urlSlug
  ) {
    return {
      redirect: {
        destination: '/services/appraisal',
        permanent: false,
      },
    };
  }

  const entryConfidence =
    typeof query.entryConfidence === 'string'
      ? decodeURI(query.entryConfidence)
      : undefined;

  const prefetchedAgents = agents?.filter((agent) => !!agent?.id) || [];

  const preselectedAgents = prefetchedAgents?.length
    ? prefetchedAgents.map(({ id, name, profilePhoto }) => ({
        id: id as unknown as string, // Lying to Typescript, we need to update prop types in fe-pa-appraisal-funnel to expect an Integer here
        name,
        avatar: profilePhoto,
      }))
    : undefined;

  const userId = req.user?.userId;

  // Coming from old dashboard URLs or having platform equals "ios-webview"
  const isWebView =
    !!query.dashboardRedirect || query.platform === 'ios-webview';

  const baseUrl = config.get<string>('BASE_URL');
  const appPrefix = config.get<string>('APPRAISAL_PREFIX');
  const graphqlEndpoint = config.get<string>('GRAPHQL_URL_CLIENT');

  const downloadPropertyReportEndpoint = config.get<string>(
    'DOWNLOAD_PROPERTY_REPORT_URL',
  );

  const title = 'Request An Appraisal';
  const description =
    'Send your property details to selected agents to receive property appraisals.';

  const defaultStep = 'address';

  return serializeNextProps<AppraisalProps>({
    // _app props
    serverInfo: res.locals.serverInfo,
    appName,
    mixpanelToken: `${config.get('MIXPANEL_TOKEN')}`,

    // Template props
    title,
    description,
    metaTitle: createMetaTitle(prefetchedAgents),
    metaDescription: prefetchedAgents?.length
      ? 'Want to know what your home is worth in a changing market?'
      : description,
    metaImage:
      'https://static.domain.com.au/content/fe-static/@domain-group/fe-pa-seller-landing/static/guide-to-selling-Illustration_a91dcd42.png',
    digitalData: createDigitalData({
      pageInfo: {
        brand: 'domain',
        pageName: 'Request An Appraisal',
        pageId: entryStep || defaultStep,
        sysEnv: 'desktop',
        isEmbeddedApp: isWebView,
        property: {
          ...(propertyAddress ? { address: propertyAddress?.label } : {}),
        },
      },
      category: {
        primaryCategory: 'Request An Appraisal',
        subCategory1: entryStep || defaultStep,
        pageType: 'Funnel',
      },
      user,
    }),
    trackingDisabled:
      typeof req.user?.disableUserTracking === 'boolean'
        ? req.user?.disableUserTracking
        : false,

    // Required props
    baseUrl,
    validatePhoneNoEndpoint: `/${appPrefix}/validate-phone`,
    authUrl: `${baseUrl}/authentications`,

    // Optional props
    baseName: `/${appPrefix}/appraisal`,
    userId,
    sessionId: res.locals?.DEVICE_SESSIONID,
    exitUrl: String(query.exitUrl) || baseUrl,
    enquiryEndpoint: `/${appPrefix}/submit-appraisal`,
    claimEndpoint: `${baseUrl}/services/track-home`,
    reClaimEndpoint: `${baseUrl}/services/reclaim-appraisal-enquiry`,
    myPropertyEndpoint: `${baseUrl}/my-properties`,
    downloadPropertyReportEndpoint,
    graphqlEndpoint,
    entryStep,
    entryConfidence,
    targetAppraisalVersion,
    prefilledFunnelData: {
      ...(propertyAddress && { address: { property: propertyAddress } }),
      ...(preselectedAgents && { agents: { selected: preselectedAgents } }),
      contact: {
        firstName: req.user?.givenName,
        lastName: req.user?.familyName,
        email: req.user?.email,
      },
    },
    isWebView,
    landing: isWebView,
    // Enable server-side user info fetching
    // useCookieOnClient: false,
    // authData: {
    //   isAuthenticated: !!req.user?.userId,
    //   email: req.user?.email,
    //   userImageSrc: undefined, // TODO: Add user image here or remove avatar from page (if it would mean extra request)
    //   memberFirstName: req.user?.givenName,
    // },
  });
};
